/**
 * The browser entry point for origin configuration.
 *
 * Importing this module in either app yields {@link ORIGINS}, resolved once from
 * `import.meta.env` at module evaluation. Vite inlines `VITE_` prefixed values at
 * build time, so the deployed bundles carry whatever the Cloudflare Pages build
 * variables said (task 4.11) and local dev carries `.env.development`.
 *
 * Imports here are extensionless on purpose: `moduleResolution: "Bundler"` plus
 * Vite resolve the source file, and the Vite configs import `./origins` directly
 * through esbuild, so one style works in all three loaders.
 */

import { resolveOrigins } from './origins';
import type { Origins } from './origins';

export {
  DEFAULT_JIG_ORIGIN,
  DEFAULT_YARNS_ORIGIN,
  devServerTarget,
  normaliseOrigin,
  resolveOrigins,
} from './origins';
export type { DevServerTarget, OriginEnv, Origins } from './origins';

/**
 * Typing for the one `import.meta.env` read in the repository.
 *
 * Declared here rather than by referencing `vite/client` so the two variables that
 * matter are spelled out and typed as possibly absent, which is what makes the
 * fallback logic in `./origins.ts` typecheck under `exactOptionalPropertyTypes`.
 * Type only, so nothing survives into the bundle.
 */
declare global {
  interface ImportMetaEnv {
    /** The workbench origin. Falls back to the local dev value when unset. */
    readonly VITE_JIG_ORIGIN?: string | undefined;
    /** The partner yarn origin. Falls back to the local dev value when unset. */
    readonly VITE_YARNS_ORIGIN?: string | undefined;
  }

  interface ImportMeta {
    readonly env: ImportMetaEnv;
  }
}

/** Both origins for the currently running bundle. Frozen, resolved once. */
export const ORIGINS: Origins = resolveOrigins(import.meta.env);
